import { useState, useEffect } from 'react';
import { categories } from '../templates';
import { useAuth } from '../context/AuthContext';

// ── Colors per category ───────────────────────────────────────
const CAT_COLORS = {
  'research':       { color:'#818cf8', bg:'rgba(99,102,241,0.15)',  border:'rgba(99,102,241,0.3)'  },
  'scholarship':    { color:'#fcd34d', bg:'rgba(245,158,11,0.15)',  border:'rgba(245,158,11,0.3)'  },
  'internship':     { color:'#6ee7b7', bg:'rgba(16,185,129,0.15)', border:'rgba(16,185,129,0.3)'  },
  'recommendation': { color:'#f9a8d4', bg:'rgba(236,72,153,0.15)', border:'rgba(236,72,153,0.3)'  },
  'followup':       { color:'#7dd3fc', bg:'rgba(14,165,233,0.15)', border:'rgba(14,165,233,0.3)'  },
};

const getCatStyle = (id) => CAT_COLORS[id] || { color:'#818cf8', bg:'rgba(99,102,241,0.15)', border:'rgba(99,102,241,0.3)' };

// SOP Guide data
const sopGuide = [
  { title:'1. What is an SOP?',           content:`A Statement of Purpose (SOP) — also called Personal Statement or Letter of Motivation — is one of the most important documents in your university application. It tells admissions committees who you are, why you want to study this program, and what you plan to do with your education.\n\nA strong SOP can compensate for a lower GPA and significantly improve your admission chances.` },
  { title:'2. SOP Structure — 5 Paragraphs', content:`Paragraph 1 — Hook and Introduction\nStart with a powerful opening — a story, a problem you observed, or a turning point that led you to this field. Avoid starting with "I want to study..."\n\nParagraph 2 — Academic Background\nDescribe your undergraduate education, relevant courses, thesis, projects, and academic achievements.\n\nParagraph 3 — Research and Work Experience\nHighlight internships, research projects, publications, work experience. Be specific with results and numbers.\n\nParagraph 4 — Why This University and Program\nShow you have researched the program. Mention specific professors, labs, or courses that align with your goals.\n\nParagraph 5 — Future Goals\nClearly state your short-term and long-term career goals and how this program helps you achieve them.` },
  { title:'3. SOP Length and Format',     content:`Length: 500 to 1,000 words for most programs. Always follow the program's specific word limit.\n\nFont: Times New Roman 12pt or Arial 11pt\nSpacing: Double or 1.5 line spacing\nMargins: 1 inch on all sides\n\nTone: Professional but personal. First-person is perfectly acceptable.\nLanguage: Clear, concise, active voice.` },
  { title:'4. Common SOP Mistakes',       content:`❌ Starting with "I was born in..."\n❌ Copying templates word for word — universities detect this\n❌ Being too general — "I have always been interested in science"\n❌ Repeating your CV instead of adding context and story\n❌ Writing about childhood dreams without connecting to now\n❌ Exceeding the word limit\n❌ Poor grammar and spelling — always proofread multiple times\n❌ Not customizing for each university` },
  { title:'5. SOP Tips to Stand Out',     content:`✅ Tell a specific story that led you to this field\n✅ Mention specific professor names and their research work\n✅ Show progression — how each experience built on the last\n✅ Quantify achievements — "improved model accuracy by 15%"\n✅ Address any weaknesses proactively (low GPA, gap year)\n✅ Get feedback from at least 2-3 people before submitting\n✅ Write a fresh SOP for each university — customize every one\n✅ Start writing 2-3 months before the deadline` },
];

// CV Guide data
const cvGuide = [
  { title:'1. Academic CV vs Resume',          content:`An Academic CV is different from a regular resume.\n\nResume: 1-2 pages, for industry jobs\nAcademic CV: 3-5+ pages, includes all academic achievements, publications, research, conferences, and awards\n\nFor university applications (Masters, PhD), always submit an Academic CV, not a resume.` },
  { title:'2. CV Structure — Section by Section', content:`Section 1 — Personal Information\nFull name, professional email, phone with country code, LinkedIn, Google Scholar (if applicable). No photo, no age, no marital status.\n\nSection 2 — Education\nReverse chronological order. University name, degree, field, GPA/CGPA, graduation year, thesis title.\n\nSection 3 — Research Experience\nMost important for PhD. List every project with supervisor name, institution, dates, and 3-4 bullet points of achievements.\n\nSection 4 — Work Experience\nInternships and jobs related to your field. Use action verbs.\n\nSection 5 — Publications\nJournal papers, conference papers, posters. Proper citation format.\n\nSection 6 — Skills\nProgramming languages, software, lab techniques, languages spoken.\n\nSection 7 — Awards and Honors\nScholarships, dean's list, competition prizes.\n\nSection 8 — References\n2-3 professors or supervisors. Get permission first.` },
  { title:'3. CV Formatting Rules',             content:`Font: Times New Roman 11-12pt or Calibri 11pt\nMargins: 1 inch on all sides\nLength: 2 pages for Masters, 3-4 pages for PhD\nFile format: Always save and submit as PDF\nFile name: FirstName_LastName_CV.pdf\n\nHeadings: Bold, slightly larger\nBullets: Start each with an action verb\nConsistency: Same date format throughout\nSpelling: Pick British or American English — stick to one` },
  { title:'4. Action Verbs for CV',             content:`Research: Investigated, Analyzed, Designed, Developed, Evaluated, Implemented, Conducted\n\nLeadership: Led, Coordinated, Managed, Supervised, Organized, Directed, Mentored\n\nTechnical: Programmed, Built, Engineered, Optimized, Automated, Simulated, Modeled\n\nAcademic: Published, Presented, Taught, Tutored, Reviewed, Collaborated\n\nResults: Improved, Increased, Reduced, Achieved, Delivered, Streamlined` },
  { title:'5. CV Mistakes to Avoid',            content:`❌ Including a photo for Western university applications\n❌ Adding age, marital status, nationality\n❌ Using same CV for every application without customizing\n❌ Vague bullet points — "worked on projects"\n❌ Spelling mistakes and inconsistent formatting\n❌ Using "Curriculum Vitae" as the title — just put your name\n❌ Too many colors or fancy design for academic CVs\n❌ Submitting as .docx instead of PDF` },
];

// Plans that get template access
const TEMPLATE_PLANS = ['pro', 'premium'];

export default function TemplatesPage({ requireUpgrade }) {
  const { user } = useAuth();

  const [selected,     setSelected]    = useState('sop');
  const [openIndex,    setOpenIndex]   = useState(0);
  const [openCatId,    setOpenCatId]   = useState(null);
  const [selectedTmpl, setSelectedTmpl] = useState(null);
  const [loadedTmpl,   setLoadedTmpl]  = useState(null);
  const [tmplLoading,  setTmplLoading] = useState(false);
  const [copied,       setCopied]      = useState(false);

  // Check if user can access templates
  const canAccessTemplates = user && TEMPLATE_PLANS.includes(user.plan);

  useEffect(() => {
    if (!selectedTmpl) return;
    const load = async () => {
      setTmplLoading(true);
      setLoadedTmpl(null);
      try {
        const module = await selectedTmpl.loader();
        setLoadedTmpl(module.default);
      } catch (err) {
        console.error('Template load error:', err);
      }
      setTmplLoading(false);
    };
    load();
  }, [selectedTmpl]);

  const copyTemplate = () => {
    if (!loadedTmpl) return;
    const text = `Subject: ${loadedTmpl.subject}\n\n${loadedTmpl.body}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSidebarClick = (id) => {
    if (id === 'sop' || id === 'cv') {
      setSelected(id);
      setOpenCatId(null);
      setSelectedTmpl(null);
      setLoadedTmpl(null);
      setOpenIndex(0);
    } else {
      // Check template access
      if (!canAccessTemplates) {
        requireUpgrade && requireUpgrade();
        return;
      }
      setSelected(id);
      setOpenCatId(openCatId === id ? null : id);
      setSelectedTmpl(null);
      setLoadedTmpl(null);
    }
  };

  const handleTmplClick = (tmpl) => {
    if (!canAccessTemplates) {
      requireUpgrade && requireUpgrade();
      return;
    }
    setSelectedTmpl(tmpl);
    setLoadedTmpl(null);
  };

  const currentCat   = categories.find(c => c.id === selected);
  const currentStyle = currentCat ? getCatStyle(currentCat.id) : null;

  // Accordion for guides
  const renderAccordion = (data, accentColor = '#6366f1') => (
    <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
      {data.map((sec, i) => {
        const isOpen = openIndex === i;
        return (
          <div key={i} style={{background:isOpen?'#111827':'rgba(255,255,255,0.02)',borderRadius:'12px',border:`1px solid ${isOpen?accentColor:'rgba(255,255,255,0.08)'}`,overflow:'hidden',transition:'all 0.2s',boxShadow:isOpen?`0 0 0 1px ${accentColor}22`:'none'}}>
            <div onClick={()=>setOpenIndex(isOpen?null:i)} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'14px 18px',cursor:'pointer',userSelect:'none'}}>
              <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                <div style={{width:'8px',height:'8px',borderRadius:'50%',background:isOpen?accentColor:'rgba(255,255,255,0.2)',flexShrink:0,transition:'all 0.2s'}} />
                <h3 style={{fontFamily:'DM Sans,sans-serif',fontSize:'14px',fontWeight:'700',color:isOpen?'#f1f5f9':'#cbd5e1',margin:0,transition:'color 0.2s'}}>
                  {sec.title}
                </h3>
              </div>
              <div style={{width:'28px',height:'28px',borderRadius:'50%',background:isOpen?`${accentColor}33`:'rgba(255,255,255,0.05)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,transition:'all 0.2s'}}>
                <span style={{color:isOpen?accentColor:'#475569',fontSize:'18px',lineHeight:'1',display:'block',transform:isOpen?'rotate(45deg)':'none',transition:'transform 0.2s',fontWeight:'300'}}>+</span>
              </div>
            </div>
            {isOpen && (
              <div style={{padding:'0 18px 18px 36px'}}>
                <div style={{height:'1px',background:'rgba(255,255,255,0.06)',marginBottom:'14px'}} />
                <p style={{fontFamily:'Inter,sans-serif',fontSize:'14px',color:'#cbd5e1',lineHeight:'1.85',margin:0,whiteSpace:'pre-line'}}>
                  {sec.content}
                </p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div style={{display:'flex',minHeight:'calc(100vh - 68px)',background:'#060b14'}}>

      {/* ── SIDEBAR ── */}
      <div style={{width:'260px',background:'#080f1a',borderRight:'1px solid rgba(255,255,255,0.07)',display:'flex',flexDirection:'column',flexShrink:0,position:'sticky',top:'68px',height:'calc(100vh - 68px)'}}>

        {/* Header */}
        <div style={{padding:'14px 14px 10px',borderBottom:'1px solid rgba(255,255,255,0.06)',flexShrink:0}}>
          <p style={{fontFamily:'Inter,sans-serif',fontSize:'10px',fontWeight:'700',color:'#475569',textTransform:'uppercase',letterSpacing:'1.5px',margin:0}}>
            TEMPLATES & GUIDES
          </p>
        </div>

        <div style={{overflowY:'auto',flex:1,padding:'8px 0'}}>

          {/* SOP Guide — always visible */}
          <div
            onClick={() => handleSidebarClick('sop')}
            style={{display:'flex',alignItems:'center',gap:'10px',padding:'10px 14px',cursor:'pointer',background:selected==='sop'?'rgba(99,102,241,0.15)':'transparent',borderLeft:selected==='sop'?'3px solid #818cf8':'3px solid transparent',transition:'all 0.15s',marginBottom:'1px'}}
            onMouseEnter={e=>{if(selected!=='sop')e.currentTarget.style.background='rgba(255,255,255,0.04)';}}
            onMouseLeave={e=>{if(selected!=='sop')e.currentTarget.style.background='transparent';}}
          >
            <div style={{width:'30px',height:'30px',borderRadius:'8px',background:selected==='sop'?'rgba(99,102,241,0.3)':'rgba(255,255,255,0.06)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'15px',flexShrink:0}}>📝</div>
            <div>
              <div style={{fontFamily:'Inter,sans-serif',fontSize:'13px',color:selected==='sop'?'#ffffff':'#e2e8f0',fontWeight:selected==='sop'?'700':'400'}}>SOP Guide</div>
              <div style={{fontFamily:'Inter,sans-serif',fontSize:'11px',color:'#475569'}}>Statement of Purpose</div>
            </div>
          </div>

          {/* CV Guide — always visible */}
          <div
            onClick={() => handleSidebarClick('cv')}
            style={{display:'flex',alignItems:'center',gap:'10px',padding:'10px 14px',cursor:'pointer',background:selected==='cv'?'rgba(99,102,241,0.15)':'transparent',borderLeft:selected==='cv'?'3px solid #818cf8':'3px solid transparent',transition:'all 0.15s',marginBottom:'1px'}}
            onMouseEnter={e=>{if(selected!=='cv')e.currentTarget.style.background='rgba(255,255,255,0.04)';}}
            onMouseLeave={e=>{if(selected!=='cv')e.currentTarget.style.background='transparent';}}
          >
            <div style={{width:'30px',height:'30px',borderRadius:'8px',background:selected==='cv'?'rgba(99,102,241,0.3)':'rgba(255,255,255,0.06)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'15px',flexShrink:0}}>📄</div>
            <div>
              <div style={{fontFamily:'Inter,sans-serif',fontSize:'13px',color:selected==='cv'?'#ffffff':'#e2e8f0',fontWeight:selected==='cv'?'700':'400'}}>CV Guide</div>
              <div style={{fontFamily:'Inter,sans-serif',fontSize:'11px',color:'#475569'}}>Academic CV Writing</div>
            </div>
          </div>

          {/* Divider + Templates Label */}
          <div style={{padding:'10px 14px 6px'}}>
            <div style={{height:'1px',background:'rgba(255,255,255,0.06)',marginBottom:'8px'}} />
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <span style={{fontFamily:'Inter,sans-serif',fontSize:'10px',fontWeight:'700',color:'#334155',textTransform:'uppercase',letterSpacing:'1px'}}>
                Email Templates
              </span>
              {!canAccessTemplates && (
                <span style={{fontFamily:'Inter,sans-serif',fontSize:'10px',fontWeight:'600',background:'rgba(245,158,11,0.15)',color:'#fbbf24',padding:'2px 8px',borderRadius:'20px',border:'1px solid rgba(245,158,11,0.3)'}}>
                  Pro+
                </span>
              )}
            </div>
          </div>

          {/* Template Categories */}
          {categories.map(cat => {
            const cs       = getCatStyle(cat.id);
            const isActive = selected === cat.id;
            const locked   = !canAccessTemplates;
            return (
              <div key={cat.id}>
                <div
                  onClick={() => handleSidebarClick(cat.id)}
                  style={{display:'flex',alignItems:'center',gap:'10px',padding:'10px 14px',cursor:'pointer',background:isActive?`${cs.bg}`:'transparent',borderLeft:isActive?`3px solid ${cs.color}`:'3px solid transparent',transition:'all 0.15s',marginBottom:'1px',opacity:locked?0.5:1}}
                  onMouseEnter={e=>{if(!isActive)e.currentTarget.style.background='rgba(255,255,255,0.04)';}}
                  onMouseLeave={e=>{if(!isActive)e.currentTarget.style.background='transparent';}}
                >
                  <div style={{width:'30px',height:'30px',borderRadius:'8px',background:isActive?cs.bg:'rgba(255,255,255,0.06)',border:isActive?`1px solid ${cs.border}`:'1px solid rgba(255,255,255,0.08)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'14px',flexShrink:0}}>
                    {locked ? '🔒' : cat.icon}
                  </div>
                  <div style={{flex:1,overflow:'hidden'}}>
                    <div style={{fontFamily:'Inter,sans-serif',fontSize:'12px',color:isActive?'#ffffff':'#e2e8f0',fontWeight:isActive?'700':'400',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                      {cat.name}
                    </div>
                    <div style={{fontFamily:'Inter,sans-serif',fontSize:'11px',color:isActive?cs.color:'#475569'}}>
                      {cat.templates.length} templates
                    </div>
                  </div>
                  {!locked && (
                    <span style={{color:'#475569',fontSize:'11px',flexShrink:0}}>
                      {openCatId===cat.id ? '▾' : '▸'}
                    </span>
                  )}
                </div>

                {/* Template dropdown */}
                {openCatId === cat.id && !locked && (
                  <div style={{background:'rgba(0,0,0,0.2)',borderBottom:`1px solid ${cs.border}`}}>
                    {cat.templates.map(t => (
                      <div
                        key={t.id}
                        onClick={() => handleTmplClick(t)}
                        style={{padding:'8px 14px 8px 54px',cursor:'pointer',background:selectedTmpl?.id===t.id?cs.bg:'transparent',borderLeft:selectedTmpl?.id===t.id?`3px solid ${cs.color}`:'3px solid transparent',transition:'all 0.15s'}}
                        onMouseEnter={e=>{if(selectedTmpl?.id!==t.id)e.currentTarget.style.background='rgba(255,255,255,0.03)';}}
                        onMouseLeave={e=>{if(selectedTmpl?.id!==t.id)e.currentTarget.style.background='transparent';}}
                      >
                        <span style={{fontFamily:'Inter,sans-serif',fontSize:'12px',color:selectedTmpl?.id===t.id?cs.color:'#94a3b8',lineHeight:'1.5',display:'block'}}>
                          {t.name}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── RIGHT CONTENT ── */}
      <div style={{flex:1,overflowY:'auto',padding:'32px 44px',background:'#060b14'}}>

        {/* SOP Guide */}
        {selected === 'sop' && (
          <>
            <div style={{marginBottom:'28px',paddingBottom:'22px',borderBottom:'1px solid rgba(255,255,255,0.07)'}}>
              <div style={{display:'flex',alignItems:'center',gap:'14px',marginBottom:'10px'}}>
                <div style={{width:'52px',height:'52px',borderRadius:'14px',background:'rgba(99,102,241,0.2)',border:'1px solid rgba(99,102,241,0.3)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'24px'}}>📝</div>
                <div>
                  <h1 style={{fontFamily:'Space Grotesk,sans-serif',fontSize:'24px',fontWeight:'700',color:'#f1f5f9',margin:'0 0 4px'}}>SOP Writing Guide</h1>
                  <p style={{fontFamily:'Inter,sans-serif',fontSize:'13px',color:'#64748b',margin:0}}>Complete guide to writing a Statement of Purpose that gets you admitted</p>
                </div>
              </div>
              <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
                {['What is SOP','Structure','Format','Mistakes','Tips'].map((tag,i) => {
                  const colors = ['rgba(99,102,241,0.15)','rgba(16,185,129,0.15)','rgba(245,158,11,0.15)','rgba(239,68,68,0.15)','rgba(14,165,233,0.15)'];
                  const texts  = ['#a5b4fc','#6ee7b7','#fcd34d','#fca5a5','#7dd3fc'];
                  return <span key={tag} style={{fontFamily:'Inter,sans-serif',fontSize:'11px',fontWeight:'600',background:colors[i],color:texts[i],padding:'4px 12px',borderRadius:'20px'}}>{tag}</span>;
                })}
              </div>
            </div>
            {renderAccordion(sopGuide, '#818cf8')}
          </>
        )}

        {/* CV Guide */}
        {selected === 'cv' && (
          <>
            <div style={{marginBottom:'28px',paddingBottom:'22px',borderBottom:'1px solid rgba(255,255,255,0.07)'}}>
              <div style={{display:'flex',alignItems:'center',gap:'14px',marginBottom:'10px'}}>
                <div style={{width:'52px',height:'52px',borderRadius:'14px',background:'rgba(16,185,129,0.15)',border:'1px solid rgba(16,185,129,0.3)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'24px'}}>📄</div>
                <div>
                  <h1 style={{fontFamily:'Space Grotesk,sans-serif',fontSize:'24px',fontWeight:'700',color:'#f1f5f9',margin:'0 0 4px'}}>CV Writing Guide</h1>
                  <p style={{fontFamily:'Inter,sans-serif',fontSize:'13px',color:'#64748b',margin:0}}>Complete guide to writing an Academic CV for PhD, Masters and internship applications</p>
                </div>
              </div>
              <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
                {['CV vs Resume','Structure','Formatting','Action Verbs','Mistakes'].map((tag,i) => {
                  const colors = ['rgba(16,185,129,0.15)','rgba(99,102,241,0.15)','rgba(245,158,11,0.15)','rgba(14,165,233,0.15)','rgba(239,68,68,0.15)'];
                  const texts  = ['#6ee7b7','#a5b4fc','#fcd34d','#7dd3fc','#fca5a5'];
                  return <span key={tag} style={{fontFamily:'Inter,sans-serif',fontSize:'11px',fontWeight:'600',background:colors[i],color:texts[i],padding:'4px 12px',borderRadius:'20px'}}>{tag}</span>;
                })}
              </div>
            </div>
            {renderAccordion(cvGuide, '#10b981')}
          </>
        )}

        {/* Template Category Grid */}
        {currentCat && !selectedTmpl && (
          <>
            <div style={{marginBottom:'28px',paddingBottom:'22px',borderBottom:'1px solid rgba(255,255,255,0.07)'}}>
              <div style={{display:'flex',alignItems:'center',gap:'14px',marginBottom:'10px'}}>
                <div style={{width:'52px',height:'52px',borderRadius:'14px',background:currentStyle?.bg,border:`1px solid ${currentStyle?.border}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'24px'}}>
                  {currentCat.icon}
                </div>
                <div>
                  <h1 style={{fontFamily:'Space Grotesk,sans-serif',fontSize:'24px',fontWeight:'700',color:'#f1f5f9',margin:'0 0 4px'}}>
                    {currentCat.name}
                  </h1>
                  <p style={{fontFamily:'Inter,sans-serif',fontSize:'13px',color:'#64748b',margin:0}}>
                    {currentCat.templates.length} ready-to-use templates — click to preview and copy
                  </p>
                </div>
              </div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:'14px'}}>
              {currentCat.templates.map(t => (
                <div
                  key={t.id}
                  onClick={() => handleTmplClick(t)}
                  style={{background:'rgba(255,255,255,0.02)',borderRadius:'14px',padding:'20px',border:`1px solid ${currentStyle?.border||'rgba(255,255,255,0.08)'}`,cursor:'pointer',transition:'all 0.2s'}}
                  onMouseEnter={e=>{e.currentTarget.style.background=currentStyle?.bg;e.currentTarget.style.transform='translateY(-2px)';}}
                  onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,0.02)';e.currentTarget.style.transform='translateY(0)';}}
                >
                  <div style={{display:'flex',alignItems:'center',gap:'10px',marginBottom:'10px'}}>
                    <span style={{fontSize:'20px'}}>{currentCat.icon}</span>
                    <h3 style={{fontFamily:'DM Sans,sans-serif',fontSize:'14px',fontWeight:'700',color:'#e2e8f0',margin:0}}>{t.name}</h3>
                  </div>
                  <p style={{fontFamily:'Inter,sans-serif',fontSize:'12px',color:'#475569',margin:'0 0 12px'}}>Click to preview and copy this template</p>
                  <span style={{fontFamily:'Inter,sans-serif',fontSize:'12px',fontWeight:'600',color:currentStyle?.color}}>
                    View Template →
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Template Detail */}
        {selectedTmpl && (
          <>
            <button
              onClick={() => { setSelectedTmpl(null); setLoadedTmpl(null); }}
              style={{display:'flex',alignItems:'center',gap:'6px',background:'transparent',border:'1px solid rgba(255,255,255,0.08)',borderRadius:'8px',color:'#94a3b8',fontFamily:'Inter,sans-serif',fontSize:'13px',padding:'8px 14px',cursor:'pointer',marginBottom:'24px'}}
            >
              ← Back to {currentCat?.name}
            </button>

            {tmplLoading && (
              <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'200px',color:'#475569',fontFamily:'Inter,sans-serif',fontSize:'14px',gap:'12px'}}>
                <div style={{width:'24px',height:'24px',border:`3px solid ${currentStyle?.color||'#6366f1'}44`,borderTop:`3px solid ${currentStyle?.color||'#6366f1'}`,borderRadius:'50%',animation:'spin 0.8s linear infinite'}} />
                <style>{`@keyframes spin{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}`}</style>
                Loading template...
              </div>
            )}

            {!tmplLoading && loadedTmpl && (
              <div style={{background:'#0d1117',borderRadius:'16px',border:`1px solid ${currentStyle?.border||'rgba(255,255,255,0.08)'}`,overflow:'hidden'}}>

                {/* Header */}
                <div style={{padding:'20px 24px',borderBottom:'1px solid rgba(255,255,255,0.06)',display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:'16px',flexWrap:'wrap',background:currentStyle?.bg}}>
                  <div>
                    <h2 style={{fontFamily:'Space Grotesk,sans-serif',fontSize:'18px',fontWeight:'700',color:'#f1f5f9',margin:'0 0 6px'}}>
                      {loadedTmpl.name}
                    </h2>
                    <div style={{fontFamily:'Inter,sans-serif',fontSize:'12px',color:'#64748b'}}>
                      <strong style={{color:'#94a3b8'}}>Subject:</strong> {loadedTmpl.subject}
                    </div>
                  </div>
                  <button
                    onClick={copyTemplate}
                    style={{padding:'10px 20px',background:copied?'#10b981':currentStyle?.color||'#6366f1',border:'none',borderRadius:'10px',color:'#fff',fontFamily:'Inter,sans-serif',fontSize:'13px',fontWeight:'600',cursor:'pointer',transition:'background 0.2s',whiteSpace:'nowrap',flexShrink:0}}
                  >
                    {copied ? '✓ Copied!' : '📋 Copy Template'}
                  </button>
                </div>

                {/* Subject */}
                <div style={{padding:'14px 24px',borderBottom:'1px solid rgba(255,255,255,0.06)',background:'rgba(255,255,255,0.02)'}}>
                  <p style={{fontFamily:'Inter,sans-serif',fontSize:'11px',color:'#475569',margin:'0 0 4px',textTransform:'uppercase',letterSpacing:'0.5px'}}>Email Subject Line</p>
                  <p style={{fontFamily:'Inter,sans-serif',fontSize:'14px',color:currentStyle?.color||'#a5b4fc',margin:0,fontWeight:'500'}}>
                    {loadedTmpl.subject}
                  </p>
                </div>

                {/* Body */}
                <div style={{padding:'24px'}}>
                  <p style={{fontFamily:'Inter,sans-serif',fontSize:'11px',color:'#475569',margin:'0 0 14px',textTransform:'uppercase',letterSpacing:'0.5px'}}>Email Body</p>
                  <div style={{fontFamily:'Inter,sans-serif',fontSize:'14px',color:'#cbd5e1',lineHeight:'1.9',whiteSpace:'pre-wrap',background:'#080f1a',borderRadius:'10px',padding:'20px',border:'1px solid rgba(255,255,255,0.06)'}}>
                    {loadedTmpl.body}
                  </div>
                </div>

                {/* Tips */}
                <div style={{padding:'0 24px 24px'}}>
                  <div style={{background:currentStyle?.bg||'rgba(99,102,241,0.08)',borderRadius:'10px',padding:'14px',border:`1px solid ${currentStyle?.border||'rgba(99,102,241,0.15)'}`}}>
                    <p style={{fontFamily:'Inter,sans-serif',fontSize:'13px',color:currentStyle?.color||'#818cf8',fontWeight:'600',margin:'0 0 6px'}}>
                      💡 How to use this template
                    </p>
                    <p style={{fontFamily:'Inter,sans-serif',fontSize:'13px',color:'#64748b',margin:0,lineHeight:'1.7'}}>
                      Replace all text in [square brackets] with your own information. Customize for each professor — never send the same email twice. Personalization significantly improves reply rates.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Default */}
        {selected !== 'sop' && selected !== 'cv' && !currentCat && (
          <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'60vh',flexDirection:'column',gap:'16px'}}>
            <span style={{fontSize:'52px'}}>✉️</span>
            <p style={{fontFamily:'Inter,sans-serif',fontSize:'16px',color:'#64748b',textAlign:'center',margin:0}}>
              Select a guide or template category from the sidebar
            </p>
          </div>
        )}

      </div>
    </div>
  );
}